'use client';

/**
 * Events Calendar Page
 * Consumer-facing event discovery and information
 */

import Link from 'next/link';
import Image from 'next/image';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Clock, Info, ArrowRight } from 'lucide-react';
import { events } from '@/lib/data';
import { format } from 'date-fns';

export default function EventsPage() {
  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      upcoming: 'bg-primary text-primary-foreground',
      'happening-now': 'bg-secondary text-secondary-foreground',
      completed: 'bg-muted text-muted-foreground',
    };
    return colors[status] || 'bg-muted';
  };

  const upcomingEvents = events.filter((e) => e.status === 'upcoming');

  return (
    <div className="min-h-screen">
      {/* Hero Header */}
      <section className="relative py-24 bg-gradient-to-br from-secondary/10 via-background to-accent/10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <h1 className="text-5xl md:text-7xl font-black mb-6 uppercase">
              Upcoming <span className="text-gradient-sunset">Events</span>
            </h1>
            <p className="text-xl md:text-2xl text-foreground/80 mb-8">
              Experience unforgettable pop-up activations, exclusive merch drops,
              and World Cup 2026 celebrations across Atlanta.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Events Grid */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="group overflow-hidden card-hover border-2 border-transparent hover:border-secondary h-full">
                  {/* Event Image */}
                  <div className="relative h-64">
                    <Image
                      src={event.image}
                      alt={event.title}
                      fill
                      className="object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-transparent" />

                    {/* Status Badge */}
                    <div className="absolute top-4 left-4">
                      <Badge className={getStatusColor(event.status)}>
                        {event.status.replace('-', ' ')}
                      </Badge>
                    </div>

                    {/* Title Overlay */}
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-black text-foreground line-clamp-2">
                        {event.title}
                      </h3>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <p className="text-muted-foreground line-clamp-2">
                      {event.description}
                    </p>

                    {/* Event Details */}
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2 text-sm">
                        <Calendar className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-bold">
                            {format(new Date(event.date), 'EEEE, MMMM d, yyyy')}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2 text-sm">
                        <Clock className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                        <p className="text-muted-foreground">{event.time}</p>
                      </div>

                      <div className="flex items-start space-x-2 text-sm">
                        <MapPin className="w-4 h-4 text-secondary mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-bold">{event.location}</p>
                          <p className="text-muted-foreground text-xs">
                            {event.address}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start space-x-2 text-sm pt-2 border-t border-border">
                        <Info className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                        <p className="text-muted-foreground">
                          {event.readinessStatus}
                        </p>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <Button
                      asChild
                      className="w-full bg-secondary hover:bg-secondary/90"
                    >
                      <Link href={`/events/${event.id}`}>
                        View Event Details
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <Card className="relative overflow-hidden bg-gradient-electric p-12 md:p-16 text-center">
            <div className="relative z-10">
              <h2 className="text-4xl md:text-6xl font-black mb-6 text-white uppercase">
                Ready for World Cup 2026?
              </h2>
              <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
                Shop exclusive Atlanta Soccer merchandise and gear up for the
                biggest sporting event in history.
              </p>
              <Button
                asChild
                size="lg"
                variant="secondary"
                className="text-lg px-8 h-14 shadow-2xl"
              >
                <Link href="/shop">
                  Shop Merch
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </Card>
        </div>
      </section>
    </div>
  );
}
